// The exchange («Біржа»): REST client of exchange-svc, the user's own transactions (approve,
// on-chain cancel, nonce bump — built by tx-proxy, signed here, sent through /tx/v1/transactions)
// and EIP-712 order signing with the key that never leaves the browser.
import api from './api';
import { Wallet } from 'ethers';
import { getHDkey, getPrivateKey } from '@/js/modules/crypto.module';
import { unlockSeed } from './sign-credentials.service';
import { auth } from '@/vuex/auth.module';
import ERC20TransactionConfirmation, { waitForTxConfirmation } from './erc20-sign.service';

const EX = 'exchange/v1';

// exchange-svc answers 429 when one address asks too often (switching pairs quickly): wait
// (Retry-After or about a second) and ask again instead of failing the whole page (2026-10-25)
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export async function get(url, config) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await api.get(url, config);
    } catch (e) {
      if (e?.response?.status !== 429 || attempt >= 3) throw e;
      const after = Number(e.response.headers?.['retry-after']);
      await sleep(after > 0 ? after * 1000 : 700 * (attempt + 1) + Math.random() * 300);
    }
  }
}

export const FLAGS = { postOnly: 1, ioc: 2, fok: 4 };
export const SIDE = { buy: 0, sell: 1 };

// ------------------------------------------------------------- public data

export const getPairs = async () => (await get(`${EX}/pairs`)).data.data || [];
export const getTicker = async () => (await get(`${EX}/ticker`)).data.data || [];
export const getOrderBook = async (pair, depth = 30) => (await get(`${EX}/orderbook/${pair}`, { params: { depth } })).data;
export const getTrades = async (pair, limit = 60) => (await get(`${EX}/trades/${pair}`, { params: { limit } })).data.data || [];
export const getCandles = async (pair, tf, from) =>
  (await get(`${EX}/candles/${pair}`, { params: { tf, from } })).data.data || [];
export const getRef = async (pair) => (await get(`${EX}/ref/${pair}`)).data;
export const getLiquidity = async () => (await get(`${EX}/liquidity`)).data;
export const getReserves = async () => (await get(`${EX}/reserves`)).data;
export const getTypedData = async () => (await get(`${EX}/typed-data`)).data;

// ------------------------------------------------------------- the user's data

export const getAssets = async () => (await get(`${EX}/me/assets`)).data.data || [];
export const getMinNonce = async () => (await get(`${EX}/me/nonce`)).data.min_nonce;
export const getMyOrders = async (status, pair) =>
  (await get(`${EX}/orders`, { params: { status, pair, limit: 200 } })).data.data || [];
export const getMyFills = async (pair) => (await get(`${EX}/fills`, { params: { pair, limit: 200 } })).data.data || [];
export const cancelOrder = async (hash) => (await api.delete(`${EX}/orders/${hash}`)).data;
export const cancelAll = async (pairId) => (await api.post(`${EX}/orders/cancel-all`, { pair_id: pairId || 0 })).data;

// ------------------------------------------------------------- signing

// credentials: { login, password } typed in the confirmation
async function walletFromCredentials(credentials) {
  const seed = await unlockSeed(credentials);
  return new Wallet(getPrivateKey(getHDkey(seed)));
}

let typedDataCache = null;
async function typedData() {
  if (!typedDataCache) typedDataCache = await getTypedData();
  return typedDataCache;
}

// a nonce that only grows: milliseconds × 10 plus a counter (incrementNonce(now) voids all before)
let lastNonce = 0n;
export function nextNonce() {
  let n = BigInt(Date.now()) * 10n;
  if (n <= lastNonce) n = lastNonce + 1n;
  lastNonce = n;
  return n;
}

function randomSalt() {
  const a = new Uint32Array(2);
  window.crypto.getRandomValues(a);
  return (BigInt(a[0]) << 21n) ^ BigInt(a[1]);
}

// Signs and sends an order. o: {pairId, side, price, amount (unit strings/bigints), flags, type, lifetimeSec}
export async function placeOrder(o, credentials) {
  const td = await typedData();
  const wallet = await walletFromCredentials(credentials);
  const maker = auth.getters.getPrivateData(auth.state).wallet;
  if (wallet.address.toLowerCase() !== String(maker).toLowerCase()) {
    throw new Error('wallet mismatch');
  }
  const value = {
    maker: wallet.address,
    pairId: Number(o.pairId),
    side: Number(o.side),
    price: o.price.toString(),
    amount: o.amount.toString(),
    nonce: nextNonce().toString(),
    expiry: Math.floor(Date.now() / 1000) + (o.lifetimeSec || 30 * 24 * 3600),
    salt: randomSalt().toString(),
    flags: Number(o.flags || 0),
  };
  const signature = await wallet._signTypedData(td.domain, td.types, value);
  const resp = await api.post(`${EX}/orders`, {
    pair_id: value.pairId,
    side: value.side,
    price: value.price,
    amount: value.amount,
    nonce: value.nonce,
    expiry: value.expiry,
    salt: value.salt,
    flags: value.flags,
    type: o.type || 'limit',
    // stop-limit: the signed order is the limit; it waits off the book until a trade reaches the stop
    stop_price: o.stopPrice ? o.stopPrice.toString() : undefined,
    maker: value.maker,
    signature,
  });
  return resp.data;
}

// ------------------------------------------------------------- the user's own transactions

export async function sendBuilt(txDetails, credentials) {
  const hash = await ERC20TransactionConfirmation(txDetails, credentials);
  const wallet = auth.getters.getPrivateData(auth.state).wallet;
  return { hash, result: await waitForTxConfirmation(hash, wallet, { timeoutMs: 60000 }) };
}

// allow ExchangeSettlement to use up to `amount` units of `asset` (USD, UAH, EUR, RE); 0 revokes
export async function approve(asset, amount, credentials) {
  const resp = await api.post('tx/v1/dex/approve', { asset, amount: amount.toString() });
  return sendBuilt(resp.data, credentials);
}

// cancel a signed order in the contract itself (works without the exchange service)
export async function cancelOnChain(order, credentials) {
  const resp = await api.post('tx/v1/dex/cancel-order', {
    order: {
      maker: auth.getters.getPrivateData(auth.state).wallet,
      pair_id: order.pair_id,
      side: order.side,
      price: order.price,
      amount: order.amount,
      nonce: order.nonce,
      expiry: order.expiry,
      salt: order.salt,
      flags: order.flags,
    },
  });
  return sendBuilt(resp.data, credentials);
}

// void every order signed before now
export async function cancelAllOnChain(credentials) {
  const resp = await api.post('tx/v1/dex/increment-nonce', { min_nonce: nextNonce().toString() });
  return sendBuilt(resp.data, credentials);
}

// ------------------------------------------------------------- WebSocket

export class ExchangeSocket {
  constructor(onMessage) {
    this.onMessage = onMessage;
    this.subs = new Set();
    this.closed = false;
    this.retry = 1000;
    this.token = null;
    this.connect();
  }

  url() {
    const base = (process.env.VUE_APP_BASE_API_URL || '/api/').replace(/\/$/, '');
    const abs = base.startsWith('http') ? base : window.location.origin + base;
    return abs.replace(/^http/, 'ws') + `/${EX}/ws`;
  }

  connect() {
    if (this.closed) return;
    try {
      this.ws = new WebSocket(this.url());
    } catch (e) {
      this.schedule();
      return;
    }
    this.ws.onopen = () => {
      this.retry = 1000;
      if (this.token) this.send({ op: 'auth', token: this.token });
      this.subs.forEach((ch) => this.send({ op: 'sub', ch }));
    };
    this.ws.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data);
        if (m.ch) this.onMessage(m.ch, m.data);
      } catch (e) {
        /* ignore */
      }
    };
    this.ws.onclose = () => this.schedule();
    this.ws.onerror = () => {
      try {
        this.ws.close();
      } catch (e) {
        /* ignore */
      }
    };
  }

  schedule() {
    if (this.closed) return;
    setTimeout(() => this.connect(), this.retry);
    this.retry = Math.min(this.retry * 2, 30000);
  }

  send(m) {
    if (this.ws && this.ws.readyState === 1) this.ws.send(JSON.stringify(m));
  }

  auth(token) {
    this.token = token;
    this.send({ op: 'auth', token });
  }

  sub(ch) {
    this.subs.add(ch);
    this.send({ op: 'sub', ch });
  }

  unsub(ch) {
    this.subs.delete(ch);
    this.send({ op: 'unsub', ch });
  }

  close() {
    this.closed = true;
    try {
      this.ws && this.ws.close();
    } catch (e) {
      /* ignore */
    }
  }
}

// error code from an exchange API failure (or a tx-proxy one)
export function errorCode(e) {
  return e?.response?.data?.code || e?.response?.data?.error_key || e?.message || 'UNKNOWN';
}
